import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { auth } from '../config/firebase'

// Types
export interface MindMapNode {
  id: string
  text: string
  x: number
  y: number
  color: string
  icon?: string
  fontSize: number
  children: MindMapNode[]
  parentId?: string
  isExpanded: boolean
  notes?: string
  links?: { url: string; title: string }[]
  shape?: 'circle' | 'rectangle'
  fontWeight?: 'normal' | 'bold'
  shadow?: boolean
  gradient?: string
  borderWidth?: number
  borderColor?: string
  opacity?: number
  image?: string
}

export interface MindMap {
  id: string
  title: string
  centerNode: MindMapNode
  theme: string
  createdAt: Date | Timestamp
  updatedAt: Date | Timestamp
  isPublic: boolean
  backgroundType?: 'solid' | 'dotted' | 'grid' | 'lines'
  backgroundColor?: string
  // Page size and custom dimensions (optional)
  pageSize?: 'A4' | 'A3' | 'Letter' | 'Custom'
  customWidth?: number
  customHeight?: number
  userId: string // Kullanıcıya özel olması için
}

const COLLECTION_NAME = 'mindMaps'

// Firestore'dan Date objelerine dönüştürme yardımcı fonksiyonu
// Daha dayanıklı: Timestamp, Date, number (ms), ISO string gibi değerleri destekler
const toJsDate = (val: any): Date | undefined => {
  if (!val) return undefined
  if (val instanceof Date) return val
  if (typeof val?.toDate === 'function') return val.toDate()
  if (typeof val === 'number') return new Date(val)
  if (typeof val === 'string') {
    const d = new Date(val)
    if (!isNaN(d.getTime())) return d
  }
  return undefined
}

// Auth hazır değilken beklemek için yardımcı
const waitForAuthUser = async (timeoutMs = 5000, intervalMs = 100) => {
  const start = Date.now()
  let user = auth.currentUser
  while (!user && Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, intervalMs))
    user = auth.currentUser
  }
  return user
}

const convertTimestamps = (data: any): any => {
  if (data && typeof data === 'object') {
    const converted: any = { ...data }
    const createdAt = toJsDate(converted.createdAt)
    const updatedAt = toJsDate(converted.updatedAt)
    if (createdAt) converted.createdAt = createdAt
    if (updatedAt) converted.updatedAt = updatedAt
    return converted
  }
  return data
}

export const mindMapService = {
  // Kullanıcının tüm zihin haritalarını getir
  getUserMindMaps: async (): Promise<MindMap[]> => {
    try {
      let user = auth.currentUser
      if (!user) {
        user = await waitForAuthUser(5000)
      }
      if (!user) {
        throw new Error('Kullanıcı oturumu bulunamadı')
      }

      // Not: Some environments throw INTERNAL ASSERTION when mixing orderBy with heterogeneous field types.
      // Avoid Firestore-side orderBy and sort on client instead for robustness.
      const q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', user.uid)
      )
      
      const querySnapshot = await getDocs(q)
      const mindMaps: MindMap[] = []
      
      querySnapshot.forEach((doc) => {
        const data = doc.data()
        mindMaps.push({
          id: doc.id,
          ...convertTimestamps(data)
        } as MindMap)
      })
      
      // Client-side sort by updatedAt desc (fallback to createdAt)
      mindMaps.sort((a: any, b: any) => {
        const ad = toJsDate(a.updatedAt) || toJsDate(a.createdAt) || new Date(0)
        const bd = toJsDate(b.updatedAt) || toJsDate(b.createdAt) || new Date(0)
        return bd.getTime() - ad.getTime()
      })
      
      return mindMaps
    } catch (error) {
      console.error('Zihin haritaları getirilemedi:', error)
      throw error
    }
  },

  // Belirli bir zihin haritasını getir
  getMindMap: async (id: string): Promise<MindMap | null> => {
    try {
      let user = auth.currentUser
      if (!user) {
        user = await waitForAuthUser(5000)
      }
      if (!user) {
        throw new Error('Kullanıcı oturumu bulunamadı')
      }

      const docRef = doc(db, COLLECTION_NAME, id)
      const docSnap = await getDoc(docRef)
      
      if (docSnap.exists()) {
        const data = docSnap.data()
        
        // Kullanıcının kendi haritası mı kontrol et
        if (data.userId !== user.uid && !data.isPublic) {
          throw new Error('Bu zihin haritasına erişim yetkiniz yok')
        }
        
        return {
          id: docSnap.id,
          ...convertTimestamps(data)
        } as MindMap
      }
      
      return null
    } catch (error) {
      console.error('Zihin haritası getirilemedi:', error)
      throw error
    }
  },

  // Yeni zihin haritası oluştur
  createMindMap: async (mindMapData: Omit<MindMap, 'id' | 'createdAt' | 'updatedAt' | 'userId'>): Promise<string> => {
    try {
      let user = auth.currentUser
      if (!user) {
        user = await waitForAuthUser(5000)
      }
      if (!user) {
        throw new Error('Kullanıcı oturumu bulunamadı')
      }

      const newMindMap = {
        ...mindMapData,
        userId: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
      // Firestore "undefined" değerleri kabul etmez; undefined olan alanları ayıkla
      const sanitizedNewMindMap = Object.fromEntries(
        Object.entries(newMindMap).filter(([_, v]) => v !== undefined)
      )
      
      const docRef = await addDoc(collection(db, COLLECTION_NAME), sanitizedNewMindMap)
      return docRef.id
    } catch (error) {
      console.error('Zihin haritası oluşturulamadı:', error)
      throw error
    }
  },

  // Zihin haritasını güncelle
  updateMindMap: async (id: string, updates: Partial<Omit<MindMap, 'id' | 'createdAt' | 'userId'>>): Promise<void> => {
    try {
      let user = auth.currentUser
      if (!user) {
        user = await waitForAuthUser(5000)
      }
      if (!user) {
        throw new Error('Kullanıcı oturumu bulunamadı')
      }

      // Önce haritanın sahibi olduğunu kontrol et
      const existingMap = await mindMapService.getMindMap(id)
      if (!existingMap || existingMap.userId !== user.uid) {
        throw new Error('Bu zihin haritasını güncelleme yetkiniz yok')
      }

      const docRef = doc(db, COLLECTION_NAME, id)
      // Firestore "undefined" değerleri kabul etmez; undefined olan alanları ayıkla (sığ)
      const sanitizedUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, v]) => v !== undefined)
      )
      await updateDoc(docRef, {
        ...sanitizedUpdates,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Zihin haritası güncellenemedi:', error)
      throw error
    }
  },

  // Zihin haritasını sil
  deleteMindMap: async (id: string): Promise<void> => {
    try {
      let user = auth.currentUser
      if (!user) {
        user = await waitForAuthUser(5000)
      }
      if (!user) {
        throw new Error('Kullanıcı oturumu bulunamadı')
      }

      // Önce haritanın sahibi olduğunu kontrol et
      const existingMap = await mindMapService.getMindMap(id)
      if (!existingMap || existingMap.userId !== user.uid) {
        throw new Error('Bu zihin haritasını silme yetkiniz yok')
      }

      const docRef = doc(db, COLLECTION_NAME, id)
      await deleteDoc(docRef)
    } catch (error) {
      console.error('Zihin haritası silinemedi:', error)
      throw error
    }
  },

  // Herkese açık zihin haritalarını getir
  getPublicMindMaps: async (limit: number = 20): Promise<MindMap[]> => {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('isPublic', '==', true)
      )
      
      const querySnapshot = await getDocs(q)
      const mindMaps: MindMap[] = []
      
      querySnapshot.forEach((doc) => {
        const data = doc.data()
        mindMaps.push({
          id: doc.id,
          ...convertTimestamps(data)
        } as MindMap)
      })
      
      // Client-side sort by updatedAt desc
      mindMaps.sort((a: any, b: any) => {
        const ad = toJsDate(a.updatedAt) || toJsDate(a.createdAt) || new Date(0)
        const bd = toJsDate(b.updatedAt) || toJsDate(b.createdAt) || new Date(0)
        return bd.getTime() - ad.getTime()
      })

      return mindMaps.slice(0, limit)
    } catch (error) {
      console.error('Herkese açık zihin haritaları getirilemedi:', error)
      throw error
    }
  }
}

export default mindMapService